import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hourglass, MapPin, Clock, Calendar, LogOut, Loader2 } from "lucide-react";

import { api } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";

/**
 * WaitlistStatus — the "You're on the waitlist" surface.
 *
 * Pulls the rider's submitted application via GET /api/me/waitlist
 * and renders the relevant snapshot. Approved riders are routed
 * straight to /; if the rider hasn't submitted an application yet,
 * we redirect to /waitlist (the form).
 */
function formatWindow(w) {
  if (!w) return "—";
  const [start, end] = w.split("-");
  const fmt = (s) => {
    const [hh, mm] = s.split(":").map((n) => parseInt(n, 10));
    const ampm = hh >= 12 ? "PM" : "AM";
    const h12 = ((hh + 11) % 12) + 1;
    return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function shortArea(addr) {
  if (!addr) return "—";
  // Show the locality-ish portion if the rider entered a full address.
  const parts = addr.split(",").map((p) => p.trim());
  if (parts.length >= 3) return parts.slice(-3, -1).join(", ");
  return addr;
}

export default function WaitlistStatus() {
  const nav = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [app, setApp] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (user.access_status === "APPROVED") { nav("/"); return; }
    (async () => {
      try {
        const { data } = await api.get("/me/waitlist");
        if (!data?.application) {
          nav("/waitlist", { replace: true });
          return;
        }
        setApp(data.application);
        setStatus(data.access_status);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  if (authLoading || loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#F9F7F4]" data-testid="waitlist-status-loading">
        <Loader2 size={20} className="animate-spin text-[#515E7B]" />
      </div>
    );
  }
  if (!app) return null;

  const rejected = status === "REJECTED";

  return (
    <div className="fixed inset-0 flex flex-col bg-[#F9F7F4]" data-testid="waitlist-status-page">
      <div className="flex-1 min-h-0 overflow-y-auto wheel-scroll">
        <div
          className="max-w-2xl mx-auto px-5 sm:px-8 pb-[max(env(safe-area-inset-bottom),96px)]"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
        >
          <div className="text-[10px] tracking-[0.26em] uppercase font-medium text-[#ed6500] mb-3 inline-flex items-center gap-2">
            <Hourglass size={11} strokeWidth={1.9} />
            {rejected ? "Application reviewed" : "On the waitlist"}
          </div>
          <h1 className="font-serif text-[34px] sm:text-[44px] leading-[1.05] tracking-tight text-[#0A1128]" data-testid="waitlist-status-headline">
            {rejected
              ? "We're not able to serve your area yet."
              : "You're on the waitlist."}
          </h1>
          <p className="mt-4 text-[15px] text-[#515E7B] max-w-prose leading-relaxed">
            {rejected
              ? "We've reviewed your application but can't activate transportation in your area at this time. We'll keep your details on file and reach out the moment that changes."
              : "We're currently building transportation coverage in your area. Every application is reviewed to ensure we can provide reliable service before activating transportation access. We'll reach out as soon as service becomes available."}
          </p>

          {/* Application snapshot */}
          <div className="mt-10 rounded-3xl bg-white border border-[#E5E1D8] p-6 sm:p-7 space-y-5" data-testid="waitlist-status-snapshot">
            <SnapshotRow icon={MapPin} label="Home area">
              {shortArea(app.home_address)}
            </SnapshotRow>
            <SnapshotRow icon={MapPin} label="Destination area">
              {shortArea(app.destination_address)}
            </SnapshotRow>
            <SnapshotRow icon={Clock} label="Pickup window">
              {formatWindow(app.pickup_window)}
            </SnapshotRow>
            <SnapshotRow icon={Clock} label="Return window">
              {formatWindow(app.return_window)}
            </SnapshotRow>
            <SnapshotRow icon={Calendar} label="Days">
              {(app.days_needed || []).join(" · ") || "—"}
            </SnapshotRow>
          </div>

          {/* Soft CTAs */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => nav("/")}
              className="h-12 rounded-2xl bg-[#0A1128] hover:bg-[#0A1128]/95 text-white text-[11px] tracking-[0.24em] uppercase font-semibold transition-colors"
              data-testid="waitlist-status-explore"
            >
              Explore Link
            </button>
            <button
              type="button"
              onClick={() => nav("/waitlist")}
              className="h-12 rounded-2xl bg-white border border-[#E5E1D8] hover:border-[#ed6500] text-[#0A1128] text-[11px] tracking-[0.24em] uppercase font-semibold transition-colors"
              data-testid="waitlist-status-edit"
            >
              Update my details
            </button>
          </div>

          <button
            type="button"
            onClick={async () => { await logout?.(); nav("/login"); }}
            className="mt-10 mx-auto block text-[11px] tracking-[0.2em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] inline-flex items-center gap-2"
            data-testid="waitlist-status-signout"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function SnapshotRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3" data-testid={`snapshot-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="shrink-0 w-9 h-9 rounded-xl bg-[#FCEADE] text-[#ed6500] flex items-center justify-center">
        <Icon size={15} strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.22em] uppercase font-medium text-[#515E7B] mb-1">{label}</div>
        <div className="text-[15px] text-[#0A1128] leading-tight">{children}</div>
      </div>
    </div>
  );
}
